import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PlFilterGroup,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getNextYearForecastAPI } from "../../../../api/reports-api";
import { toReportApiFilters } from "../filterRelationshipUtils";
import { getApiFilterKey, getGlobalPeriodHiddenKeys, formatActualsThroughFromMonths, formatActualsThroughDate, headerActualsDate, resolvePeriodCellValue } from "./plTimeFilterUtils";
import { applyNumFilter, isNumFilterActive, calcOpDropdownPos, renderStandardNumFilter, renderStandardTextFilter } from "./plTableFilterUtils";
import { OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import Loading from "../../../../common-components/Loading";
import { TH_ACTUAL_BG, TH_ACTUAL_TEXT } from "../../reportTableConfig";

/* ── Design tokens ── */
const HAIRLINE     = "#E2E8F0";
const HEADER_BG    = PL_TH_BG;
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;
const TEXT_MUTED   = "#6B7280";
const TEXT_PRIMARY = "#111827";
const ROW_STRIPE   = "#F7FAFC";
const ROW_EVEN_BG  = "#FFFFFF";

/* ══════════════════════════════════════════════════════════════ */
/*  Column definitions                                            */
/* ══════════════════════════════════════════════════════════════ */
const MONTH_COLS = [
  { key: "jan",   label: "Jan",        isTotal: false },
  { key: "feb",   label: "Feb",        isTotal: false },
  { key: "mar",   label: "Mar",        isTotal: false },
  { key: "apr",   label: "Apr",        isTotal: false },
  { key: "may",   label: "May",        isTotal: false },
  { key: "jun",   label: "Jun",        isTotal: false },
  { key: "jul",   label: "Jul",        isTotal: false },
  { key: "aug",   label: "Aug",        isTotal: false },
  { key: "sep",   label: "Sep",        isTotal: false },
  { key: "oct",   label: "Oct",        isTotal: false },
  { key: "nov",   label: "Nov",        isTotal: false },
  { key: "dec",   label: "Dec",        isTotal: false },
  { key: "total", label: "Total Year", isTotal: true  },
];

const QTR_COLS = [
  { key: "q1",    label: "Q1",         isTotal: false },
  { key: "q2",    label: "Q2",         isTotal: false },
  { key: "q3",    label: "Q3",         isTotal: false },
  { key: "q4",    label: "Q4",         isTotal: false },
  { key: "total", label: "Total Year", isTotal: true  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Row definitions                                               */
/* ══════════════════════════════════════════════════════════════ */
const ROW_DEFS = [
  { key: "contractRevenue", label: "Revenue",  isPct: false, type: "subtotal"  },
  { key: "directLabor",     label: "Direct Labor",      isPct: false, type: "normal"    },
  { key: "subcontractor",   label: "Subcontractor",     isPct: false, type: "normal"    },
  { key: "odcs",            label: "ODCs",              isPct: false, type: "normal"    },
  { key: "totalDirectCost", label: "Total Direct Cost", isPct: false, type: "subtotal"  },
  { key: "grossMargin",     label: "Gross Margin",      isPct: false, type: "subtotal"  },
  { key: "grossMarginPct",  label: "Gross Margin %",    isPct: true,  type: "pct"       },
  { key: "indirectCost",    label: "Indirect Cost",     isPct: false, type: "normal"    },
  { key: "totalCost",       label: "Total Cost",        isPct: false, type: "normal"    },
  { key: "netProfit",       label: "Net Profit / Fee",  isPct: false, type: "normal"    },
  { key: "netMarginPct",    label: "Net Margin %",      isPct: true,  type: "pct"       },
];

const ROW_STYLE = {
  normal:    { color: TEXT_MUTED,   weight: 400, italic: false },
  subtotal:  { color: TEXT_PRIMARY, weight: 700, italic: false },
  pct:       { color: TEXT_MUTED,   weight: 400, italic: true  },
};

const VIEW_OPTS = ["Month", "Quarter"];

/* ══════════════════════════════════════════════════════════════ */
/*  Filter helpers — see plTableFilterUtils.js                    */
/* ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════ */
/*  Number formatter                                              */
/* ══════════════════════════════════════════════════════════════ */
const fmtCell = (v, isPct) =>
  isPct ? `${(+v).toFixed(1)}%` : v === 0 ? "—" : `$${Math.abs(+v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

/* ══════════════════════════════════════════════════════════════ */
/*  Data helpers                                                  */
/* ══════════════════════════════════════════════════════════════ */
// API returns pct rows as ratios (0.3745 = 37.45%); multiply by 100 for display.
function normalizePct(monthly) {
  const out = { ...monthly };
  ROW_DEFS.forEach(({ key, isPct }) => {
    if (isPct && out[key]) {
      const t = {};
      MONTH_COLS.forEach(({ key: ck }) => { t[ck] = (out[key][ck] ?? 0) * 100; });
      out[key] = t;
    }
  });
  return out;
}

const QMAP = [
  ["jan", "feb", "mar"],
  ["apr", "may", "jun"],
  ["jul", "aug", "sep"],
  ["oct", "nov", "dec"],
];
const DOLLAR_KEYS = [
  "contractRevenue", "directLabor", "subcontractor", "odcs",
  "totalDirectCost", "grossMargin", "indirectCost", "totalCost", "netProfit",
];

// Quarter view is derived client-side from monthly dollar values; pct rows are recomputed as ratios.
function buildQuarterly(monthly) {
  const qtr = {};
  DOLLAR_KEYS.forEach((key) => {
    const m = monthly[key] || {};
    qtr[key] = {
      q1: QMAP[0].reduce((s, k) => s + (m[k] || 0), 0),
      q2: QMAP[1].reduce((s, k) => s + (m[k] || 0), 0),
      q3: QMAP[2].reduce((s, k) => s + (m[k] || 0), 0),
      q4: QMAP[3].reduce((s, k) => s + (m[k] || 0), 0),
      total: m.total || 0,
    };
  });
  qtr.grossMarginPct = {};
  qtr.netMarginPct   = {};
  ["q1", "q2", "q3", "q4", "total"].forEach((q) => {
    const rev = qtr.contractRevenue[q] || 0;
    qtr.grossMarginPct[q] = rev ? (qtr.grossMargin[q] / rev) * 100 : 0;
    qtr.netMarginPct[q]   = rev ? (qtr.netProfit[q]   / rev) * 100 : 0;
  });
  return qtr;
}

/* ══════════════════════════════════════════════════════════════ */
/*  API filter mapper                                             */
/* ══════════════════════════════════════════════════════════════ */
function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year", "orgId", "contractType", "contractId", "clinLvl2", "businessType", "awardType",
    "competitionType", "bu", "popEndDate", "sector", "projectId",
    "contractName", "customerName",
  ]);
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "16px 20px 14px 24px",
    borderBottom: `1px solid ${HAIRLINE}`,
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  sectionHeadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  sectionHeadActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    lineHeight: 1.4,
  },
  tableScroll: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: PL_FONT,
    fontSize: 13,
    tableLayout: "auto",
    minWidth: 600,
  },
  thLabel: {
    background: HEADER_BG, color: PL_TH_TEXT,
    padding: "13px 20px", fontWeight: 700, fontSize: 10,
    letterSpacing: "0.14em", textTransform: "uppercase",
    textAlign: "left", whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thCol: {
    background: HEADER_BG, color: PL_TH_TEXT,
    padding: "12px 8px", fontWeight: 600, fontSize: 12,
    textAlign: "right", whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thColActual: {
    background: ACTUAL_TH_BG, color: ACTUAL_TH_FG,
    padding: "12px 8px", fontWeight: 600, fontSize: 12,
    textAlign: "right", whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
    "& $filterIconBtn": {
      color: ACTUAL_TH_FG,
      opacity: 0.65,
      "&:hover": { opacity: 1, background: "rgba(0,0,0,0.08)" },
    },
    "& $filterIconBtnActive": {
      opacity: 1,
      color: ACTUAL_TH_FG,
      background: "rgba(0,0,0,0.12)",
    },
  },
  thColForecast: {
    background: HEADER_BG, color: PL_TH_TEXT,
    padding: "12px 8px", fontWeight: 600, fontSize: 12,
    textAlign: "right", whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thTotal: {
    background: HEADER_BG, color: PL_TH_TEXT,
    padding: "12px 12px", fontWeight: 700, fontSize: 12,
    textAlign: "right", whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  tdLabel: {
    padding: "9px 20px",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 12,
  },
  tdVal: {
    padding: "9px 10px",
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 12,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdTotal: {
    padding: "9px 12px",
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    color: TEXT_PRIMARY,
  },

  /* ── floating filter row ── */
  trFilter: {
    background: "#F1F5F9",
    borderBottom: `2px solid ${HAIRLINE}`,
  },
  filterLabelCell: {
    padding: "5px 10px",
    background: "#F1F5F9",
  },
  filterNumCell: {
    padding: "5px 6px",
    background: "#F1F5F9",
  },
  filterWrap: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    height: 26,
  },
  filterTextInput: {
    flex: 1,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 7px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%",
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 5px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0,
    width: 22,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: "#4F46E5",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", borderColor: "#4F46E5" },
  },
  filterOpBtnActive: {
    background: "#4F46E5",
    borderColor: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 18,
    height: 18,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },

  /* operator dropdown (rendered via portal) */
  opDropdown: {
    position: "fixed",
    zIndex: 100,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    overflow: "hidden",
    fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", color: "#4F46E5" },
  },
  opDropdownItemActive: {
    background: "#EEF2FF",
    color: "#4F46E5",
    fontWeight: 600,
  },

  /* ── column picker ── */
  colPickerWrap: {
    position: "relative",
    flexShrink: 0,
  },
  colsBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "#fff",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    fontFamily: PL_FONT,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" },
  },
  colsBtnActive: { borderColor: "#C7D2FE", background: "#EEF2FF", color: "#4F46E5" },
  hiddenBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 18,
    padding: "0 7px",
    borderRadius: 10,
    background: "#4F46E5",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
  },
  colPicker: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
    minWidth: 180,
    maxHeight: 420,
    overflowY: "auto",
    fontFamily: PL_FONT,
  },
  colPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px 8px",
    borderBottom: "1px solid #F3F4F6",
  },
  colPickerTitle: { fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.05em", textTransform: "uppercase" },
  colPickerReset: {
    fontSize: 11,
    fontWeight: 600,
    color: "#4F46E5",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { textDecoration: "underline" },
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    color: "#374151",
    userSelect: "none",
    borderBottom: "1px solid #F9FAFB",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: "#F9FAFB" },
  },
  colPickerCheck: {
    width: 14,
    height: 14,
    accentColor: "#4F46E5",
    cursor: "pointer",
    flexShrink: 0,
  },
  filterIconBtn: {
    flexShrink: 0, width: 16, height: 16, border: "none",
    background: "transparent", cursor: "pointer", padding: 0,
    borderRadius: 3, color: PL_TH_TEXT, opacity: 0.5,
    display: "flex", alignItems: "center", justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: {
    opacity: 1, color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)", borderRadius: 3,
  },
  filterPanel: {
    position: "fixed", zIndex: 99, background: "#fff",
    border: "1px solid #E2E8F0", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px", minWidth: 180, fontFamily: PL_FONT,
  },
}));

function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main component                                                */
/* ══════════════════════════════════════════════════════════════ */
export default function NextYearForecast({ filters, actualsThroughDate }) {
  const plClasses    = usePlByProgramStyles();
  const localClasses = useStyles();
  useReportFonts();
  const classes = { ...plClasses, ...localClasses };

  /* ── API state ── */
  const [monthly,     setMonthly]     = useState(null);
  const [fiscalYear,  setFiscalYear]  = useState(null);
  const [liveVersion, setLiveVersion] = useState(null);
  const [actualMonths, setActualMonths] = useState([]);
  const [actualThroughMonth, setActualThroughMonth] = useState(null);
  const [loading,     setLoading]     = useState(true);

  /* ── UI state ── */
  const [viewMode,      setViewMode]      = useState("Month");
  const [hiddenCols,    setHiddenCols]    = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [labelFilter,   setLabelFilter]   = useState("");
  const [colFilters,    setColFilters]    = useState({});
  const [openOpKey,     setOpenOpKey]     = useState(null);
  const [dropdownPos,   setDropdownPos]   = useState({ top: 0, left: 0 });
  const colPickerWrapRef = useRef(null);
  const opBtnRefs        = useRef({});
  const [openFilterKey,  setOpenFilterKey]  = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs    = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  const apiFilterKey = useMemo(() => getApiFilterKey(filters, buildApiFilters), [filters]);
  const globalPeriodHidden = useMemo(() => {
    const keys = viewMode === "Quarter"
      ? QTR_COLS.filter((c) => !c.isTotal).map((c) => c.key)
      : MONTH_COLS.filter((c) => !c.isTotal).map((c) => c.key);
    return getGlobalPeriodHiddenKeys(filters, viewMode, keys);
  }, [filters?.monthName, filters?.quarter, viewMode]);

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current     = openOpKey;     }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  /* ── Fetch ── */
  useEffect(() => {
    setLoading(true);
    getNextYearForecastAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.status === 200 && res.data) {
          const { monthly: raw, fiscalYear: fy, liveVersion: lv, actualMonths: am, actualThroughMonth: atm } = res.data;
          setMonthly(normalizePct(raw));
          setFiscalYear(fy ?? null);
          setLiveVersion(lv ?? null);
          setActualMonths(
            am?.length
              ? am
              : MONTH_COLS.slice(0, Number(atm) || 0).map((c) => c.key),
          );
          setActualThroughMonth(atm ?? null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Reset filter/col state when view mode changes ── */
  const handleViewChange = (newView) => {
    setViewMode(newView);
    setHiddenCols(new Set());
    setColFilters({});
    setLabelFilter("");
  };

  /* ── Close pickers on outside click ── */
  useEffect(() => {
    if (!colPickerOpen) return;
    const handler = (e) => {
      if (colPickerWrapRef.current && !colPickerWrapRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colPickerOpen]);

  useEffect(() => {
    if (!openOpKey) return;
    const handler = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  /* ── Derived data ── */
  const isQtr      = viewMode === "Quarter";
  const allCols    = isQtr ? QTR_COLS : MONTH_COLS;
  const dataCols   = allCols.filter((c) => !c.isTotal);
  const activeData = monthly ? (isQtr ? buildQuarterly(monthly) : monthly) : {};

  /* A selected global month/quarter filter overrides the table-level column
     picker — e.g. hiding Jan via the picker then selecting Jan globally
     should bring the Jan column back, not keep it hidden. */
  const isColHidden  = (key) => {
    if (globalPeriodHidden.has(key)) return true;
    if (globalPeriodHidden.size > 0) return false;
    return hiddenCols.has(key);
  };
  const visibleDataCols = dataCols.filter((c) => !isColHidden(c.key));
  const visibleCols     = allCols.filter((c) => !isColHidden(c.key));
  const showTotal       = !isColHidden("total");
  const hiddenCount     = allCols.filter((c) => isColHidden(c.key)).length;

  /* ── Column picker handlers ── */
  const toggleCol   = (key) => setHiddenCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const showAllCols = () => setHiddenCols(new Set());

  /* ── Filter handlers ── */
  const updateColFilter = useCallback((colKey, field, value) =>
    setColFilters((prev) => ({
      ...prev,
      [colKey]: { op: "gt", ...prev[colKey], [field]: value },
    })), []);

  const clearColFilter = useCallback((colKey) =>
    setColFilters((prev) => { const n = { ...prev }; delete n[colKey]; return n; }), []);

  const isColFilterActive = (colKey) => isNumFilterActive(colFilters[colKey]);

  const toggleOpDropdown = (colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(colKey);
  };

  const selectOp = (colKey, opKey) => {
    updateColFilter(colKey, "op", opKey);
    setOpenOpKey(null);
  };

  const cellOpts = (row) => ({
    rowKey: row.key,
    allRowData: activeData,
    isPct: row.isPct,
  });

  const rowPasses = (row) => {
    if (labelFilter && !row.label.toLowerCase().includes(labelFilter.toLowerCase())) return false;
    for (const [colKey, filter] of Object.entries(colFilters)) {
      if (!isColFilterActive(colKey)) continue;
      const rawVal = resolvePeriodCellValue(activeData[row.key], colKey, filters, viewMode, cellOpts(row));
      if (!applyNumFilter(rawVal, filter)) return false;
    }
    return true;
  };

  const getColLabel = (colKey) => allCols.find((c) => c.key === colKey)?.label || colKey;

  const renderNumFilterDiv = (colKey) => renderStandardNumFilter({
    label: getColLabel(colKey),
    colKey,
    colFilters,
    updateColFilter,
    clearColFilter,
    toggleOpDropdown,
    opBtnRefs,
  });

  const hasFilterValue = (key) => {
    if (key === "label") return !!labelFilter;
    const f = colFilters[key];
    return f && f.value !== "" && f.value !== undefined;
  };

  const renderFilterContent = (key) => {
    if (key === "label") return renderStandardTextFilter({ label: "Metric", value: labelFilter, onChange: setLabelFilter, onClear: () => setLabelFilter(""), placeholder: "Search metrics…" });
    return renderNumFilterDiv(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const fyLabel   = fiscalYear ? `FY-${fiscalYear}` : "Next Year";
  const actualSet = useMemo(() => {
    const months = new Set((actualMonths || []).map((m) => String(m).toLowerCase()));
    const cutoff = Number(actualThroughMonth) || 0;
    if (!months.size && cutoff > 0) {
      MONTH_COLS.slice(0, cutoff).forEach((c) => months.add(c.key));
    }
    /* Quarters are actual when all three months in the quarter are actual */
    const qMonths = [
      ["q1", ["jan", "feb", "mar"]],
      ["q2", ["apr", "may", "jun"]],
      ["q3", ["jul", "aug", "sep"]],
      ["q4", ["oct", "nov", "dec"]],
    ];
    qMonths.forEach(([qk, ms]) => {
      if (ms.every((m) => months.has(m))) months.add(qk);
    });
    return months;
  }, [actualMonths, actualThroughMonth]);
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(actualThroughMonth),
    formatActualsThroughFromMonths(actualMonths),
  );

  if (loading) return <Loading />;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <PlPageHeader
          classes={classes}
          eyebrow="P&L by Program"
          title="Next Year Forecast"
          accent="Forward Look"
          meta={`${fyLabel} Financial Planning · ${ROW_DEFS.length} Metrics`}
          metaTop={fyLabel}
          date={headerDate}
        />

        <div className={classes.tableSection}>
          {/* ── Section head ── */}
          <div className={classes.tableSectionHead}>
            <div className={classes.sectionHeadRow}>
              <div>
                <h2 className={classes.tableSectionTitle}>{fyLabel} Forecast</h2>
                <div className={classes.tableSectionMeta}>
                  Next Year Planning · {isQtr ? "Quarterly" : "Monthly"} View · {ROW_DEFS.length} Metrics
                </div>
              </div>
              <div className={classes.sectionHeadActions}>
                <PlFilterGroup
                  label="View"
                  options={VIEW_OPTS}
                  value={viewMode}
                  onChange={handleViewChange}
                  classes={classes}
                />
                <div className={classes.colPickerWrap} ref={colPickerWrapRef}>
                  <button
                    className={`${classes.colsBtn} ${colPickerOpen ? classes.colsBtnActive : ""}`}
                    onClick={() => setColPickerOpen((p) => !p)}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/>
                    </svg>
                    Columns
                    {hiddenCount > 0 && <span className={classes.hiddenBadge}>{hiddenCount} Hidden</span>}
                  </button>
                  {colPickerOpen && (
                    <div className={classes.colPicker}>
                      <div className={classes.colPickerHeader}>
                        <span className={classes.colPickerTitle}>Show / Hide Columns</span>
                        {hiddenCount > 0 && <button className={classes.colPickerReset} onClick={showAllCols}>Show All</button>}
                      </div>
                      {allCols.map((col) => (
                        <label key={col.key} className={classes.colPickerItem} style={globalPeriodHidden.has(col.key) ? { opacity: 0.45 } : undefined}>
                          <input
                            type="checkbox"
                            className={classes.colPickerCheck}
                            checked={!isColHidden(col.key)}
                            disabled={globalPeriodHidden.has(col.key)}
                            onChange={() => toggleCol(col.key)}
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div className={classes.tableScroll} onScroll={handleTableScroll}>
            <table className={classes.table}>
              <thead>
                {/* Header row */}
                <tr>
                  <th className={classes.thLabel}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                      <span style={{ flex: 1, color: openFilterKey === "label" ? "#60A5FA" : undefined }}>Metric</span>
                      <button ref={(el) => { filterBtnRefs.current["label"] = el; }} data-filter-btn
                        className={`${classes.filterIconBtn} ${hasFilterValue("label") ? classes.filterIconBtnActive : ""}`}
                        onClick={() => toggleFilter("label")} title="Filter">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                  {visibleDataCols.map((c) => {
                    const isActual = actualSet.has(c.key);
                    return (
                    <th key={c.key} className={isActual ? classes.thColActual : classes.thColForecast}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                        <span style={{ color: isActual ? ACTUAL_TH_FG : openFilterKey === c.key ? "#60A5FA" : undefined }}>{c.label}</span>
                        <button ref={(el) => { filterBtnRefs.current[c.key] = el; }} data-filter-btn
                          className={`${classes.filterIconBtn} ${hasFilterValue(c.key) ? classes.filterIconBtnActive : ""}`}
                          onClick={() => toggleFilter(c.key)} title="Filter">
                          <FilterFunnelIcon />
                        </button>
                      </div>
                    </th>
                    );
                  })}
                  {showTotal && (
                    <th className={classes.thTotal}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                        <span style={{ color: openFilterKey === "total" ? "#60A5FA" : undefined }}>Total</span>
                        <button ref={(el) => { filterBtnRefs.current["total"] = el; }} data-filter-btn
                          className={`${classes.filterIconBtn} ${hasFilterValue("total") ? classes.filterIconBtnActive : ""}`}
                          onClick={() => toggleFilter("total")} title="Filter">
                          <FilterFunnelIcon />
                        </button>
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {ROW_DEFS.map((row, idx) => {
                  const rs      = ROW_STYLE[row.type];
                  const bg      = idx % 2 === 1 ? ROW_STRIPE : ROW_EVEN_BG;
                  const rowData = activeData[row.key] || {};
                  const visible = rowPasses(row);
                  return (
                    <tr key={row.key} style={visible ? {} : { display: "none" }}>
                      <td
                        className={classes.tdLabel}
                        style={{ background: bg, color: rs.color, fontWeight: rs.weight, fontStyle: rs.italic ? "italic" : "normal" }}
                      >
                        {row.label}
                      </td>
                      {visibleDataCols.map((c) => (
                        <td
                          key={c.key}
                          className={classes.tdVal}
                          style={{ background: bg, color: rs.color, fontWeight: rs.weight, fontStyle: rs.italic ? "italic" : "normal" }}
                        >
                          {rowData[c.key] != null ? fmtCell(rowData[c.key], row.isPct) : ""}
                        </td>
                      ))}
                      {showTotal && (
                        <td
                          className={classes.tdTotal}
                          style={{ background: bg, fontStyle: rs.italic ? "italic" : "normal" }}
                        >
                          {fmtCell(resolvePeriodCellValue(rowData, "total", filters, viewMode, cellOpts(row)), row.isPct)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Operator dropdown — rendered at body level to escape overflow clipping */}
            {openOpKey && createPortal(
              <OpDropdownContent
                activeOp={(colFilters[openOpKey]?.op) || "gt"}
                onSelect={(op) => selectOp(openOpKey, op)}
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
              />,
              document.body
            )}
            {openFilterKey && createPortal(
              <div data-filter-panel className={classes.filterPanel} style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
                {renderFilterContent(openFilterKey)}
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
